import { useState, type ComponentProps, type MouseEvent, type ReactNode } from 'react'
import clsx from 'clsx'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    type ButtonProps
} from '@material-ui/core'
import Link, { type LinkProps } from '@material-ui/core/Link'
import { makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import type i18n from 'i18next'
import Suggest from '../../containers/Apartment/Stepper/Steps/Suggest'

const useStyles = makeStyles(() => ({
    triggerButton: {
        textTransform: 'none',
        fontWeight: 600,
        textDecoration: 'underline',
        color: '#94004A',
        '&:hover': {
            textDecoration: 'underline',
            background: 'transparent'
        }
    },
    triggerLink: {
        fontWeight: 600,
        textDecoration: 'underline',
        color: '#94004A',
        cursor: 'pointer',
        '&:hover': {
            background: 'transparent'
        }
    },
    dialogTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px'
    },
    dialogHeading: {
        fontWeight: 700,
        fontSize: 20,
        lineHeight: '28px',
        color: '#0F172A',
        marginRight: 12
    },
    closeButton: {
        marginLeft: 'auto',
        color: '#94004A',
        '& svg': {
            fontSize: '1.75rem'
        }
    },
    detailsWrapper: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 16,
        marginTop: 24
    },
    details: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
        padding: '24px 32px',
        background: '#F7F8F9',
        borderRadius: 20
    },
    detailsTitle: {
        fontWeight: 700,
        fontSize: 18,
        lineHeight: '26px',
        color: '#0F172A'
    },
    detailsText: {
        fontSize: 16,
        lineHeight: '24px',
        color: '#1F2937'
    }
}))

type DetailSectionConfig = {
    key?: string
    titleKey: string
    titleParams?: Record<string, unknown>
    bodyKey: string
    bodyParams?: Record<string, unknown>
}

type TriggerRenderProps = {
    label: string
    onClick: (event: MouseEvent<any>) => void
    className: string
    buttonProps: Record<string, unknown>
    dialogId: string
}

type GenericTriggerProps = {
    onClick?: (event: MouseEvent<any>) => void
    className?: string
} & Record<string, unknown>

type DigivakuusFaqVariant = 'landlord' | 'afterShowing' | 'rentInsurance'

type VariantConfig = {
    labelKeys: string[]
    titleKeys: string[]
    contentKeys: string[]
    detailsSections: ReadonlyArray<DetailSectionConfig>
    showDialogActionClose: boolean
    triggerVariant: 'button' | 'inline-link'
    buttonProps?: Partial<ButtonProps>
    linkProps?: Partial<LinkProps>
    dialogProps?: Partial<ComponentProps<typeof Dialog>>
    dialogActionsProps?: Partial<ComponentProps<typeof DialogActions>>
    closeButtonProps?: Partial<ButtonProps>
}

export interface DigivakuusFaqLinkProps {
    t: i18n.TFunction
    variant?: DigivakuusFaqVariant
    depositMonthlyAmountText?: string
    buttonClassName?: string
    inlineWrapperClassName?: string
    buttonProps?: Partial<ButtonProps>
    linkProps?: Partial<LinkProps>
    suggestProps?: Partial<ComponentProps<typeof Suggest>>
    labelKeys?: string[]
    titleKeys?: string[]
    contentKeys?: string[]
    detailsSections?: ReadonlyArray<DetailSectionConfig>
    closeLabelKey?: string
    renderTrigger?: (props: TriggerRenderProps) => ReactNode
    dialogId?: string
    dialogProps?: Partial<ComponentProps<typeof Dialog>>
    dialogTitleProps?: Partial<ComponentProps<typeof DialogTitle>>
    dialogContentProps?: Partial<ComponentProps<typeof DialogContent>>
    dialogActionsProps?: Partial<ComponentProps<typeof DialogActions>>
    closeButtonProps?: Partial<ButtonProps>
    showDialogActionClose?: boolean
    triggerVariant?: 'button' | 'inline-link'
    dialogHeadingClassName?: string
    closeIconButtonClassName?: string
    detailsWrapperClassName?: string
    detailsClassName?: string
    detailsTitleClassName?: string
    detailsTextClassName?: string
}

const variantConfigs: Record<DigivakuusFaqVariant, VariantConfig> = {
    landlord: {
        labelKeys: ['afterShowing.digivakuusInfoLink'],
        titleKeys: ['afterShowing.digivakuusInfoLandlordBold', 'afterShowing.digivakuusInfoBold'],
        contentKeys: ['afterShowing.digivakuusInfoLandlord', 'afterShowing.digivakuusInfo'],
        detailsSections: [
            {
                key: 'landlord-details-1',
                titleKey: 'afterShowing.digivakuusInfoLandlordDetailsTitle',
                bodyKey: 'afterShowing.digivakuusInfoLandlordDetailsBody'
            },
            {
                key: 'landlord-details-2',
                titleKey: 'afterShowing.digivakuusInfoLandlordDetailsTitle2',
                bodyKey: 'afterShowing.digivakuusInfoLandlordDetailsBody2'
            },
            {
                key: 'landlord-details-3',
                titleKey: 'afterShowing.digivakuusInfoLandlordDetailsTitle3',
                bodyKey: 'afterShowing.digivakuusInfoLandlordDetailsBody3'
            },
            {
                key: 'landlord-details-4',
                titleKey: 'afterShowing.digivakuusInfoLandlordDetailsTitle4',
                bodyKey: 'afterShowing.digivakuusInfoLandlordDetailsBody4'
            },
            {
                key: 'landlord-details-5',
                titleKey: 'afterShowing.digivakuusInfoLandlordDetailsTitle5',
                bodyKey: 'afterShowing.digivakuusInfoLandlordDetailsBody5'
            },
            {
                key: 'landlord-details-6',
                titleKey: 'afterShowing.digivakuusInfoLandlordDetailsTitle6',
                bodyKey: 'afterShowing.digivakuusInfoLandlordDetailsBody6'
            }
        ],
        showDialogActionClose: false,
        triggerVariant: 'button',
        buttonProps: {
            type: 'button'
        }
    },
    afterShowing: {
        labelKeys: ['afterShowing.digivakuusInfoLink'],
        titleKeys: ['afterShowing.digivakuusInfoBold'],
        contentKeys: ['afterShowing.digivakuusInfo'],
        detailsSections: [
            {
                key: 'after-showing-details-0',
                titleKey: 'afterShowing.digivakuusInfoDetailsTitle',
                bodyKey: 'afterShowing.digivakuusInfoDetailsBody'
            },
            {
                key: 'after-showing-details-1',
                titleKey: 'afterShowing.digivakuusInfoDetailsTitle1',
                bodyKey: 'afterShowing.digivakuusInfoDetailsBody1'
            },
            {
                key: 'after-showing-details-2',
                titleKey: 'afterShowing.digivakuusInfoDetailsTitle2',
                bodyKey: 'afterShowing.digivakuusInfoDetailsBody2'
            },
            {
                key: 'after-showing-details-3',
                titleKey: 'afterShowing.digivakuusInfoDetailsTitle3',
                bodyKey: 'afterShowing.digivakuusInfoDetailsBody3'
            },
            {
                key: 'after-showing-details-4',
                titleKey: 'afterShowing.digivakuusInfoDetailsTitle4',
                bodyKey: 'afterShowing.digivakuusInfoDetailsBody4'
            },
            {
                key: 'after-showing-details-5',
                titleKey: 'afterShowing.digivakuusInfoDetailsTitle5',
                bodyKey: 'afterShowing.digivakuusInfoDetailsBody5'
            }
        ],
        showDialogActionClose: true,
        triggerVariant: 'inline-link',
        linkProps: {
            component: 'button',
            underline: 'always'
        },
        closeButtonProps: {
            color: 'primary'
        }
    },
    rentInsurance: {
        labelKeys: [
            'apartments.rented.rentInsuranceFaqLink',
            'rentInsuranceFaqLink',
            'afterShowing.digivakuusInfoLink'
        ],
        titleKeys: [
            'apartments.rented.rentInsuranceFaqTitle',
            'rentInsuranceFaqTitle'
        ],
        contentKeys: [
            'apartments.rented.rentInsuranceFaqContent',
            'rentInsuranceFaqContent'
        ],
        detailsSections: [
            {
                key: 'rent-insurance-details-1',
                titleKey: 'apartments.rented.rentInsuranceFaqDetailsTitle',
                bodyKey: 'apartments.rented.rentInsuranceFaqDetailsBody'
            },
            {
                key: 'rent-insurance-details-2',
                titleKey: 'apartments.rented.rentInsuranceFaqDetailsTitle2',
                bodyKey: 'apartments.rented.rentInsuranceFaqDetailsBody2'
            },
            {
                key: 'rent-insurance-details-3',
                titleKey: 'apartments.rented.rentInsuranceFaqDetailsTitle3',
                bodyKey: 'apartments.rented.rentInsuranceFaqDetailsBody3'
            },
            {
                key: 'rent-insurance-details-4',
                titleKey: 'apartments.rented.rentInsuranceFaqDetailsTitle4',
                bodyKey: 'apartments.rented.rentInsuranceFaqDetailsBody4'
            },
            {
                key: 'rent-insurance-details-5',
                titleKey: 'apartments.rented.rentInsuranceFaqDetailsTitle5',
                bodyKey: 'apartments.rented.rentInsuranceFaqDetailsBody5'
            },
            {
                key: 'rent-insurance-details-6',
                titleKey: 'apartments.rented.rentInsuranceFaqDetailsTitle6',
                bodyKey: 'apartments.rented.rentInsuranceFaqDetailsBody6'
            }
        ],
        showDialogActionClose: false,
        triggerVariant: 'button',
        buttonProps: {
            type: 'button'
        }
    }
}

const DigivakuusFaqLink = ({
    t,
    variant = 'landlord',
    depositMonthlyAmountText,
    buttonClassName,
    inlineWrapperClassName,
    buttonProps,
    linkProps,
    suggestProps,
    labelKeys,
    titleKeys,
    contentKeys,
    detailsSections,
    closeLabelKey,
    renderTrigger,
    dialogId: dialogIdProp,
    dialogProps,
    dialogTitleProps,
    dialogContentProps,
    dialogActionsProps,
    closeButtonProps,
    showDialogActionClose,
    triggerVariant,
    dialogHeadingClassName,
    closeIconButtonClassName,
    detailsWrapperClassName,
    detailsClassName,
    detailsTitleClassName,
    detailsTextClassName
}: DigivakuusFaqLinkProps) => {
    const classes = useStyles()
    const [open, setOpen] = useState(false)

    const variantConfig = variantConfigs[variant]
    const monthlyAmount = (depositMonthlyAmountText || '-').toString()

    const resolveFirstTranslation = (keys?: string[]) => {
        if (!keys) return ''
        for (const key of keys) {
            if (!key) continue
            const value = ((t(key) as string) || '').trim()
            if (value) return value
        }
        return ''
    }

    const dialogId = dialogIdProp || `digivakuus-faq-dialog-${variant}`
    const ariaLabelId = `${dialogId}-title`
    const ariaDescId = `${dialogId}-description`

    const faqLinkLabel = resolveFirstTranslation(labelKeys ?? variantConfig.labelKeys)
    if (!faqLinkLabel) {
        return null
    }

    const faqTitle = resolveFirstTranslation(titleKeys ?? variantConfig.titleKeys)
    const faqContent = resolveFirstTranslation(contentKeys ?? variantConfig.contentKeys)
    const closeLabel = resolveFirstTranslation([closeLabelKey ?? 'general.close']) || 'Close'

    const shouldAutoInjectAmount = (bodyKey: string, hasExplicitParams: boolean) =>
        !hasExplicitParams &&
        (variant === 'landlord' || variant === 'afterShowing') &&
        bodyKey.includes('Body3')

    const resolvedDetails = (detailsSections ?? variantConfig.detailsSections)
        .map((section, index) => {
            const key = section.key ?? `detail-${index}`
            const title = ((t(section.titleKey, section.titleParams || {}) as string) || '').trim()
            const bodyParams =
                section.bodyParams ||
                (shouldAutoInjectAmount(section.bodyKey, !!section.bodyParams)
                    ? { amount: monthlyAmount }
                    : undefined)
            const body = ((t(section.bodyKey, bodyParams || {}) as string) || '').trim()

            if (!title && !body) {
                return null
            }

            return { key, title, body }
        })
        .filter(Boolean) as Array<{ key: string; title: string; body: string }>

    const resolvedTriggerVariant = triggerVariant ?? variantConfig.triggerVariant

    const mergedTriggerProps: GenericTriggerProps =
        resolvedTriggerVariant === 'inline-link'
            ? { ...(variantConfig.linkProps ?? {}), ...(linkProps ?? {}) }
            : { ...(variantConfig.buttonProps ?? {}), ...(buttonProps ?? {}) }

    const {
        onClick: providedTriggerOnClick,
        className: providedTriggerClassName,
        ...restTriggerProps
    } = mergedTriggerProps

    const triggerClassName = clsx(
        resolvedTriggerVariant === 'inline-link' ? classes.triggerLink : classes.triggerButton,
        buttonClassName,
        providedTriggerClassName
    )

    const handleTriggerClick = (event: MouseEvent<any>) => {
        providedTriggerOnClick?.(event)
        if (!event.isDefaultPrevented()) {
            setOpen(true)
        }
    }

    const mergedDialogProps = {
        ...(variantConfig.dialogProps ?? {}),
        ...(dialogProps ?? {})
    }

    const {
        onClose: externalDialogOnClose,
        open: _dialogOpenProp,
        ...restDialogProps
    } = mergedDialogProps

    const mergedCloseButtonProps: GenericTriggerProps = {
        ...(variantConfig.closeButtonProps ?? {}),
        ...(closeButtonProps ?? {})
    }

    const {
        onClick: providedCloseButtonOnClick,
        className: closeButtonClassNameProp,
        ...restCloseButtonProps
    } = mergedCloseButtonProps

    const mergedDialogActionsProps = {
        ...(variantConfig.dialogActionsProps ?? {}),
        ...(dialogActionsProps ?? {})
    }

    const {
        className: dialogActionsClassNameProp,
        ...restDialogActionsProps
    } = mergedDialogActionsProps

    const { className: dialogTitleClassNameProp, ...restDialogTitleProps } = dialogTitleProps || {}
    const { className: dialogContentClassNameProp, ...restDialogContentProps } = dialogContentProps || {}

    const resolvedShowDialogActionClose =
        showDialogActionClose ?? variantConfig.showDialogActionClose

    const handleClose = () => setOpen(false)

    const handleFooterCloseClick = (event: MouseEvent<any>) => {
        providedCloseButtonOnClick?.(event)
        if (!event.isDefaultPrevented()) {
            setOpen(false)
        }
    }

    const handleDialogClose: ComponentProps<typeof Dialog>['onClose'] = (event, reason) => {
        externalDialogOnClose?.(event, reason)
        if (!event.isDefaultPrevented()) {
            setOpen(false)
        }
    }

    const headingClassName = clsx(classes.dialogHeading, dialogHeadingClassName)
    const closeIconClassName = clsx(classes.closeButton, closeIconButtonClassName)
    const detailsWrapperClass = clsx(classes.detailsWrapper, detailsWrapperClassName)
    const detailsClass = clsx(classes.details, detailsClassName)
    const detailsTitleClass = clsx(classes.detailsTitle, detailsTitleClassName)
    const detailsTextClass = clsx(classes.detailsText, detailsTextClassName)
    const dialogTitleClassName = clsx(classes.dialogTitle, dialogTitleClassNameProp)
    const dialogContentClassName = clsx(dialogContentClassNameProp)
    const dialogActionsClassName = clsx(dialogActionsClassNameProp)
    const closeButtonClassName = clsx(closeButtonClassNameProp)

    const finalSuggestProps: ComponentProps<typeof Suggest> = {
        isModal: true,
        containerMaxWidth: '100%',
        contentMaxWidth: '100%',
        padding: '0',
        ...suggestProps,
        content: faqContent
    }

    const triggerProps: TriggerRenderProps = {
        label: faqLinkLabel,
        onClick: handleTriggerClick,
        className: triggerClassName,
        buttonProps: restTriggerProps,
        dialogId
    }

    const triggerElement = renderTrigger
        ? renderTrigger(triggerProps)
        : resolvedTriggerVariant === 'inline-link'
            ? (() => {
                const linkNode = (
                    <Link
                        onClick={handleTriggerClick}
                        className={triggerClassName}
                        aria-haspopup='dialog'
                        aria-controls={dialogId}
                        {...(restTriggerProps as LinkProps)}
                    >
                        {faqLinkLabel}
                    </Link>
                )
                return inlineWrapperClassName
                    ? <span className={inlineWrapperClassName}>{linkNode}</span>
                    : linkNode
            })()
            : (
                <Button
                    onClick={handleTriggerClick}
                    className={triggerClassName}
                    aria-haspopup='dialog'
                    aria-controls={dialogId}
                    {...(restTriggerProps as ButtonProps)}
                >
                    {faqLinkLabel}
                </Button>
            )

    return (
        <>
            {triggerElement}
            <Dialog
                open={open}
                onClose={handleDialogClose}
                aria-labelledby={ariaLabelId}
                aria-describedby={ariaDescId}
                fullWidth
                maxWidth='md'
                {...restDialogProps}
            >
                <DialogTitle
                    disableTypography
                    className={dialogTitleClassName}
                    id={ariaLabelId}
                    {...restDialogTitleProps}
                >
                    {!!faqTitle && (
                        <Typography variant='h6' className={headingClassName}>
                            {faqTitle}
                        </Typography>
                    )}
                    <IconButton
                        aria-label={closeLabel}
                        onClick={handleClose}
                        className={closeIconClassName}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    id={ariaDescId}
                    className={dialogContentClassName}
                    {...restDialogContentProps}
                >
                    <Suggest {...finalSuggestProps} />
                    {resolvedDetails.length > 0 && (
                        <div className={detailsWrapperClass}>
                            {resolvedDetails.map(({ key, title, body }) => (
                                <div key={key} className={detailsClass}>
                                    {title && (
                                        <Typography component='h3' className={detailsTitleClass}>
                                            {title}
                                        </Typography>
                                    )}
                                    {body && (
                                        <Typography component='p' className={detailsTextClass}>
                                            {body}
                                        </Typography>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
                {resolvedShowDialogActionClose && (
                    <DialogActions
                        className={dialogActionsClassName}
                        {...restDialogActionsProps}
                    >
                        <Button
                            onClick={handleFooterCloseClick}
                            className={closeButtonClassName}
                            {...(restCloseButtonProps as ButtonProps)}
                        >
                            {closeLabel}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        </>
    )
}

export default DigivakuusFaqLink
